/**
 * v1.1 — Routes a tap on an `auto_release_confirm` push notification into a
 * callback (which opens ConfirmAutoReleaseDialog). Three input paths:
 *
 *  1. Native (iOS/Android Capacitor) — listens to PushNotifications plugin's
 *     `pushNotificationActionPerformed` event.
 *  2. PWA cold-launch — Service Worker's notificationclick opens a new
 *     window with `?ar_confirm=<id>` query param. We read it on mount and
 *     strip it from the URL.
 *  3. PWA app-already-open — Service Worker postMessage's the existing
 *     client with `{ type: 'auto_release_confirm', releaseId }`. We listen
 *     on the service-worker message channel.
 *
 * All three converge on the same `onConfirm(releaseId)` callback.
 */
import { useEffect } from 'react';
import { isNativePlatform } from '../lib/runtime';

interface ActionPayload {
  notification?: {
    data?: Record<string, string | undefined>;
  };
}

export function useAutoReleaseConfirmHandler(onConfirm: (releaseId: string) => void): void {
  // ── Native path (Capacitor) ──────────────────────────────────────────────
  useEffect(() => {
    if (!isNativePlatform()) return;

    const PushNotifications: any = (globalThis as any).Capacitor?.Plugins?.PushNotifications;
    if (!PushNotifications) return;

    let removeListener: (() => void | Promise<void>) | null = null;

    // Capacitor's plugin contract for addListener changed between major
    // versions: v5 returned Promise<PluginListenerHandle>, v6 returns the
    // handle directly. Wrap in Promise.resolve so either shape works.
    const handleOrPromise = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPayload) => {
        const data = action?.notification?.data || {};
        if (data.event === 'auto_release_confirm' && data.release_id) {
          onConfirm(data.release_id);
        }
      },
    );

    Promise.resolve(handleOrPromise)
      .then((handle: { remove: () => void | Promise<void> }) => {
        removeListener = () => handle.remove();
      })
      .catch(() => { /* registration is best-effort */ });

    return () => {
      void removeListener?.();
    };
  }, [onConfirm]);

  // ── PWA cold-launch path: read ?ar_confirm=<id> from the URL ─────────────
  // Runs once on mount. The Service Worker's notificationclick handler opens
  // the PWA with this query string when no window was already open. We then
  // strip the param so a refresh doesn't re-open the dialog.
  useEffect(() => {
    if (isNativePlatform()) return; // native uses the Capacitor path above
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('ar_confirm');
    if (!id) return;
    onConfirm(id);
    // Clean the URL so back/forward doesn't re-trigger the dialog.
    params.delete('ar_confirm');
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }, [onConfirm]);

  // ── PWA app-already-open path: Service Worker postMessage ────────────────
  useEffect(() => {
    if (isNativePlatform()) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (data && data.type === 'auto_release_confirm' && typeof data.releaseId === 'string') {
        onConfirm(data.releaseId);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [onConfirm]);
}
